import { PDFViewer, PDFDownloadLink, pdf } from '@react-pdf/renderer';
import { Download, Save, AlertCircle } from 'lucide-react';
import React, { useState } from 'react';
import styles from './PDFExportDialog.module.css';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import DocumentPDFRenderer from '../pdf/DocumentPDFRenderer';

/**
 * Enhanced PDF Export Dialog with Advanced Markdown Support
 * 
 * This dialog now leverages the MarkdownRendererToPdf component which provides:
 * - Full Mermaid diagram rendering in PDF
 * - Rich markdown formatting (bold, italic, code, tables, lists)
 * - Advanced typography with font family selection
 * - Professional styling with customizable headers and footers
 * - Proper handling of sections, headings, and content structure
 * - Support for blockquotes, code blocks, and inline formatting
 * - Image embedding and SVG diagram support
 * 
 * The PDF output maintains the same high-quality rendering as the MarkdownRenderer
 * component used in the main application, ensuring consistency between web view
 * and PDF export.
 */

interface PDFExportDialogProps {
  visible: boolean;
  onHide: () => void;
  onSave: (filename: string, pdfBlob: Blob) => void;
  title: string;
  sections: { id: string; title: string; content: string }[];
  headerTemplate?: string;
  footerTemplate?: string;
  styleSettings?: {
    fontFamily?: string;
    fontSize?: string;
    lineSpacing?: string;
    marginTop?: string;
    marginBottom?: string;
    marginLeft?: string;
    marginRight?: string;
    pageSize?: string;
    orientation?: string;
  };
  resources?: { id: string; name: string; type: string; url?: string }[];
  disabledSections?: Record<string, boolean>;
}

const PDFExportDialog: React.FC<PDFExportDialogProps> = ({
  visible,
  onHide,
  onSave,
  title,
  sections,
  headerTemplate,
  footerTemplate,
  styleSettings: initialStyleSettings,
  resources,
  disabledSections = {}
}) => {
  const [filename, setFilename] = useState('');
  const [filenameError, setFilenameError] = useState('');
  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [includeHeader, setIncludeHeader] = useState(true);
  const [includeFooter, setIncludeFooter] = useState(true);
  const [styleSettings, setStyleSettings] = useState({...initialStyleSettings});
  
  // Reset state when dialog opens, but maintain section order
  React.useEffect(() => {
    if (visible) {
      setFilename((title || 'Untitled').replace(/[^a-zA-Z0-9]/g, ''));
      // Select all non-disabled sections by default, but keep original order
      setSelectedSectionIds(
        sections
          .filter(section => !disabledSections[section.id])
          .map(section => section.id)
      );
      setStyleSettings({...initialStyleSettings});
      setIncludeHeader(true);
      setIncludeFooter(true);
      setFilenameError('');
    }
  }, [visible, title, sections, disabledSections, initialStyleSettings]);
  
  // Validate filename
  const handleFilenameChange = (value: string) => {
    const sanitized = value.replace(/[^a-zA-Z0-9]/g, '');
    setFilename(sanitized);
    
    if (sanitized !== value) {
      setFilenameError('Filename can only contain letters and numbers');
    } else {
      setFilenameError('');
    }
  };  // Filter sections based on selection, but preserve original order (memoized)
  const filteredSections = React.useMemo(() => 
    sections.filter(section => selectedSectionIds.includes(section.id)),
    [sections, selectedSectionIds]
  );
  // Memoize style settings to prevent unnecessary re-renders
  const memoizedStyleSettings = React.useMemo(() => styleSettings, [JSON.stringify(styleSettings)]);

  // Memoize the PDF document to prevent unnecessary re-renders
  const pdfDocument = React.useMemo(() => (
    <DocumentPDFRenderer 
      sections={filteredSections}
      headerTemplate={includeHeader ? headerTemplate : ""}
      footerTemplate={includeFooter ? footerTemplate : ""}
      title={title}
      styleSettings={memoizedStyleSettings}
      resources={resources}
    />
  ), [filteredSections, includeHeader, headerTemplate, includeFooter, footerTemplate, title, memoizedStyleSettings, resources]);
  
  // Handle style setting change
  const updateStyleSetting = (key: string, value: string) => {
    setStyleSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  // Handle save button click
  const handleSave = async () => {
    if (!filename) {
      setFilenameError('Please enter a filename');
      return;
    }
    
    setIsLoading(true);
      try {
      // Generate PDF blob using memoized document
      const blob = await pdf(pdfDocument).toBlob();
      onSave(filename, blob);
      onHide();
    } catch (error) {
      console.error('Error generating PDF:', error);
      setFilenameError('Error generating PDF. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!visible) return null;
  
  return (
    <Dialog open={visible} onOpenChange={onHide}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex flex-col" style={{ height: '85vh' }}>
        <DialogHeader>
          <DialogTitle>Export Document as PDF</DialogTitle>
          <DialogDescription>
            Create a PDF from your document. Select which sections to include.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="preview" className="flex-1 flex flex-col min-h-0 mt-4">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="preview">Preview</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          
          <TabsContent value="preview" className="flex-1 min-h-0 flex flex-col">            <div className={`flex-1 h-full min-h-0 border rounded ${styles['pdf-preview-container']}`}>
              <PDFViewer width="100%" height="100%" style={{ border: 'none' }}>
                {pdfDocument}
              </PDFViewer>
            </div>
          </TabsContent>
          
          <TabsContent value="settings" className="flex-1 min-h-0 overflow-auto p-1">
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="pdf-filename">Filename (letters and numbers only)</Label>
                <Input 
                  id="pdf-filename" 
                  value={filename}
                  onChange={(e) => handleFilenameChange(e.target.value)}
                  placeholder="EnterFilenameWithoutSpaces"
                  className={filenameError ? "border-red-300" : ""}
                />
                {filenameError && (
                  <div className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {filenameError}
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Document Content</Label>
                <div className="border rounded-md p-4 space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="include-header"
                        checked={includeHeader}
                        onCheckedChange={(checked) => setIncludeHeader(checked === true)}
                      />
                      <Label htmlFor="include-header">Include header</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="include-footer"
                        checked={includeFooter}
                        onCheckedChange={(checked) => setIncludeFooter(checked === true)}
                      />
                      <Label htmlFor="include-footer">Include footer</Label>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Sections to Include</Label>
                    <div className="max-h-[200px] overflow-y-auto space-y-2 pl-4">
                      {sections.map((section) => (
                        <div key={section.id} className="flex items-center space-x-2">
                          <Checkbox 
                            id={`section-${section.id}`}
                            checked={selectedSectionIds.includes(section.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedSectionIds([...selectedSectionIds, section.id]);
                              } else {
                                setSelectedSectionIds(selectedSectionIds.filter(id => id !== section.id));
                              }
                            }}
                            disabled={disabledSections[section.id]}
                          />
                          <Label 
                            htmlFor={`section-${section.id}`}
                            className={disabledSections[section.id] ? 'text-gray-400' : ''}
                          >
                            {section.title}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Document Appearance</Label>
                <div className="border rounded-md p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="font-family">Font Family</Label>
                      <Select 
                        value={styleSettings?.fontFamily || 'Times New Roman'}
                        onValueChange={(value) => updateStyleSetting('fontFamily', value)}
                      >
                        <SelectTrigger id="font-family">
                          <SelectValue placeholder="Select font" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                          <SelectItem value="Arial">Arial</SelectItem>
                          <SelectItem value="Helvetica">Helvetica</SelectItem>
                          <SelectItem value="Courier">Courier</SelectItem>
                          <SelectItem value="Calibri">Calibri</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="font-size">Font Size</Label>
                      <Select 
                        value={styleSettings?.fontSize || '12pt'}
                        onValueChange={(value) => updateStyleSetting('fontSize', value)}
                      >
                        <SelectTrigger id="font-size">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10pt">10pt</SelectItem>
                          <SelectItem value="11pt">11pt</SelectItem>
                          <SelectItem value="12pt">12pt</SelectItem>
                          <SelectItem value="14pt">14pt</SelectItem>
                          <SelectItem value="16pt">16pt</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="line-spacing">Line Spacing</Label>
                      <Select 
                        value={styleSettings?.lineSpacing || '1.5'}
                        onValueChange={(value) => updateStyleSetting('lineSpacing', value)}
                      >
                        <SelectTrigger id="line-spacing">
                          <SelectValue placeholder="Select spacing" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Single</SelectItem>
                          <SelectItem value="1.15">1.15</SelectItem>
                          <SelectItem value="1.5">1.5</SelectItem>
                          <SelectItem value="2">Double</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="page-size">Page Size</Label>
                      <Select 
                        value={styleSettings?.pageSize || 'A4'}
                        onValueChange={(value) => updateStyleSetting('pageSize', value)}
                      >
                        <SelectTrigger id="page-size">
                          <SelectValue placeholder="Select page size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A4">A4</SelectItem>
                          <SelectItem value="Letter">Letter</SelectItem>
                          <SelectItem value="Legal">Legal</SelectItem>
                          <SelectItem value="A3">A3</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="orientation">Orientation</Label>
                      <Select 
                        value={styleSettings?.orientation || 'portrait'}
                        onValueChange={(value) => updateStyleSetting('orientation', value)}
                      >
                        <SelectTrigger id="orientation">
                          <SelectValue placeholder="Select orientation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="portrait">Portrait</SelectItem>
                          <SelectItem value="landscape">Landscape</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2 pt-2">
                    <Label>Margins</Label>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="space-y-1">
                        <Label htmlFor="margin-top" className="text-xs">Top</Label>
                        <Select 
                          value={styleSettings?.marginTop || '1in'}
                          onValueChange={(value) => updateStyleSetting('marginTop', value)}
                        >
                          <SelectTrigger id="margin-top">
                            <SelectValue placeholder="Top" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0.5in">0.5 in</SelectItem>
                            <SelectItem value="0.75in">0.75 in</SelectItem>
                            <SelectItem value="1in">1 in</SelectItem>
                            <SelectItem value="1.25in">1.25 in</SelectItem>
                            <SelectItem value="1.5in">1.5 in</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="margin-right" className="text-xs">Right</Label>
                        <Select 
                          value={styleSettings?.marginRight || '1in'}
                          onValueChange={(value) => updateStyleSetting('marginRight', value)}
                        >
                          <SelectTrigger id="margin-right">
                            <SelectValue placeholder="Right" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0.5in">0.5 in</SelectItem>
                            <SelectItem value="0.75in">0.75 in</SelectItem>
                            <SelectItem value="1in">1 in</SelectItem>
                            <SelectItem value="1.25in">1.25 in</SelectItem>
                            <SelectItem value="1.5in">1.5 in</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="margin-bottom" className="text-xs">Bottom</Label>
                        <Select 
                          value={styleSettings?.marginBottom || '1in'}
                          onValueChange={(value) => updateStyleSetting('marginBottom', value)}
                        >
                          <SelectTrigger id="margin-bottom">
                            <SelectValue placeholder="Bottom" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0.5in">0.5 in</SelectItem>
                            <SelectItem value="0.75in">0.75 in</SelectItem>
                            <SelectItem value="1in">1 in</SelectItem>
                            <SelectItem value="1.25in">1.25 in</SelectItem>
                            <SelectItem value="1.5in">1.5 in</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="margin-left" className="text-xs">Left</Label>
                        <Select 
                          value={styleSettings?.marginLeft || '1in'}
                          onValueChange={(value) => updateStyleSetting('marginLeft', value)}
                        >
                          <SelectTrigger id="margin-left">
                            <SelectValue placeholder="Left" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0.5in">0.5 in</SelectItem>
                            <SelectItem value="0.75in">0.75 in</SelectItem>
                            <SelectItem value="1in">1 in</SelectItem>
                            <SelectItem value="1.25in">1.25 in</SelectItem>
                            <SelectItem value="1.5in">1.5 in</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onHide}>Cancel</Button>            <PDFDownloadLink 
            document={pdfDocument}
            fileName={`${filename || 'document'}.pdf`}
            style={{ textDecoration: 'none' }}
          >
            <Button variant="outline" disabled={!filename}>
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
          </PDFDownloadLink>
          <Button onClick={handleSave} disabled={isLoading || !filename}>
            <Save className="mr-2 h-4 w-4" />
            Save to Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PDFExportDialog;
